import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faPhone,
  faGlobe,
  faChevronDown,
  faChevronUp,
  faCheckCircle,
  faExclamationTriangle,
  faListUl,
} from "@fortawesome/free-solid-svg-icons";
import "./css/OreInsightCard.css";

/**
 * OreInsightCard — Displays ORE (Organization Rules Engine) enrichment
 * in a simple, non-overwhelming format for the end user.
 *
 * Design Principles:
 *   - Only appears when ORE found a matching organization
 *   - Summary line always visible: "We checked [Org]'s real communication rules"
 *   - Official contact info prominently shown (the #1 action item)
 *   - What Should You Do? shown as simple, friendly bullet points
 *   - Detailed signals collapsed by default (opt-in for curious users)
 *   - Warm, reassuring tone — not clinical or alarming
 *
 * Props:
 *   @param {Object}   oreData          - The ORE enrichment fields from the API response
 *   @param {string}   channel          - "email" | "sms" | "social" — affects wording
 *   @param {boolean}  isMobile         - Renders in compact mobile accordion style
 *   @param {string}   accordionPrefix  - CSS class prefix for mobile accordion (e.g. "easm", "sasm")
 */
const OreInsightCard = ({
  oreData,
  channel = "email",
  isMobile = false,
  accordionPrefix = "",
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // ── Guard: only render if ORE actually matched an org ──
  if (!oreData || !oreData.oreEnhanced || !oreData.resolvedOrgName) {
    return null;
  }

  const orgName = oreData.resolvedOrgName;
  const officialPhone = oreData.officialPhone;
  const officialWebsite = oreData.officialWebsite;
  const recommendedActions = oreData.recommendedActions || [];
  const riskLevel = oreData.oreRiskLevel;
  const finalScore = oreData.finalRiskScore;
  const oreRiskFactors = oreData.oreRiskFactors || [];

  // Determine severity class for the card border
  const getSeverityClass = () => {
    if (!riskLevel) return "ore-info";
    switch (riskLevel) {
      case "CRITICAL":
      case "HIGH":
        return "ore-danger";
      case "MEDIUM":
        return "ore-warning";
      case "LOW":
      case "MINIMAL":
        return "ore-safe";
      default:
        return "ore-info";
    }
  };

  // Friendly channel label
  const getChannelLabel = () => {
    switch (channel) {
      case "sms":
        return "text message";
      case "social":
        return "social media post";
      case "email":
      default:
        return "email";
    }
  };

  // Build the friendly summary line
  const getSummaryText = () => {
    if (riskLevel === "CRITICAL" || riskLevel === "HIGH") {
      return `We checked how ${orgName} actually communicates — this ${getChannelLabel()} doesn't match their real practices.`;
    }
    if (riskLevel === "MEDIUM") {
      return `We checked ${orgName}'s communication rules — some things about this ${getChannelLabel()} are unusual.`;
    }
    return `We verified this against ${orgName}'s known communication practices.`;
  };

  // ── Mobile accordion rendering ──
  if (isMobile && accordionPrefix) {
    return (
      <div
        className={`${accordionPrefix}-accordion ore-insight-mobile ${showDetails ? "open" : ""} ${getSeverityClass()}`}
      >
        <button
          className={`${accordionPrefix}-accordion-header ore-insight-header`}
          onClick={() => setShowDetails(!showDetails)}
          type="button"
        >
          <span className={`${accordionPrefix}-accordion-title`}>
            <FontAwesomeIcon icon={faShieldAlt} className="ore-shield-icon" />
            {orgName} — Verified Rules
          </span>
          <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} />
        </button>
        {showDetails && (
          <div className={`${accordionPrefix}-accordion-body`}>
            <OreInsightContent
              orgName={orgName}
              officialPhone={officialPhone}
              officialWebsite={officialWebsite}
              recommendedActions={recommendedActions}
              oreRiskFactors={oreRiskFactors}
              summaryText={getSummaryText()}
              severityClass={getSeverityClass()}
              finalScore={finalScore}
              riskLevel={riskLevel}
              channel={channel}
            />
          </div>
        )}
      </div>
    );
  }

  // ── Desktop card rendering ──
  return (
    <div className={`common-form-group ore-insight-card ${getSeverityClass()}`}>
      <h4
        className="ore-insight-title"
        onClick={() => setShowDetails(!showDetails)}
        style={{ cursor: "pointer" }}
      >
        <FontAwesomeIcon
          icon={faShieldAlt}
          className="fa-icon-margin-right-8 ore-shield-icon"
        />
        Organization Verification — {orgName}
        <FontAwesomeIcon
          icon={showDetails ? faChevronUp : faChevronDown}
          className="ore-toggle-icon"
        />
      </h4>

      {/* Always-visible summary */}
      <div className="ore-summary-line">
        <p>{getSummaryText()}</p>
      </div>

      {/* Always-visible: official contact info (most important for users) */}
      {(officialPhone || officialWebsite) && (
        <div className="ore-official-contacts">
          <p className="ore-contacts-label">
            <FontAwesomeIcon icon={faCheckCircle} className="ore-check-icon" />
            To verify, contact {orgName} directly:
          </p>
          <div className="ore-contact-items">
            {officialPhone && (
              <a
                href={`tel:${officialPhone}`}
                className="ore-contact-item ore-phone"
              >
                <FontAwesomeIcon icon={faPhone} />
                <span>{officialPhone}</span>
              </a>
            )}
            {officialWebsite && (
              <a
                href={officialWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="ore-contact-item ore-website"
              >
                <FontAwesomeIcon icon={faGlobe} />
                <span>{officialWebsite}</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Expandable details */}
      {showDetails && (
        <OreInsightContent
          orgName={orgName}
          officialPhone={officialPhone}
          officialWebsite={officialWebsite}
          recommendedActions={recommendedActions}
          oreRiskFactors={oreRiskFactors}
          summaryText={null}
          severityClass={getSeverityClass()}
          finalScore={finalScore}
          riskLevel={riskLevel}
          channel={channel}
        />
      )}

      {/* "See more / less" toggle text */}
      {(recommendedActions.length > 0 || oreRiskFactors.length > 0) && (
        <button
          className="ore-details-toggle"
          onClick={() => setShowDetails(!showDetails)}
          type="button"
        >
          {showDetails ? "Show less" : "See what we recommend"}
        </button>
      )}
    </div>
  );
};

/**
 * Inner content for both mobile and desktop views.
 */
const OreInsightContent = ({
  orgName,
  recommendedActions,
  oreRiskFactors,
  finalScore,
  riskLevel,
}) => {
  const [showSignals, setShowSignals] = useState(false);

  return (
    <div className="ore-insight-content">
      {/* What Should You Do? (friendly language) */}
      {recommendedActions.length > 0 && (
        <div className="ore-actions">
          <p className="ore-actions-label">
            <FontAwesomeIcon
              icon={faListUl}
              className="fa-icon-margin-right-8"
            />
            What We Recommend
          </p>
          <ul className="ore-actions-list">
            {recommendedActions.map((action, i) => (
              <li key={i} className="ore-action-item">
                {action}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk score meter (visual, non-technical) */}
      {finalScore != null && (
        <div className="ore-risk-meter">
          <div className="ore-risk-meter-label">
            <span>Risk Level</span>
            <span
              className={`ore-risk-badge ore-risk-${(riskLevel || "minimal").toLowerCase()}`}
            >
              {riskLevel || "UNKNOWN"}
            </span>
          </div>
          <div className="ore-risk-bar-track">
            <div
              className="ore-risk-bar-fill"
              style={{ width: `${Math.min(finalScore, 100)}%` }}
            />
          </div>
          <div className="ore-risk-bar-labels">
            <span>Safe</span>
            <span>Dangerous</span>
          </div>
        </div>
      )}

      {/* Detailed signals — hidden behind another toggle */}
      {oreRiskFactors.length > 0 && (
        <div className="ore-signals-section">
          <button
            className="ore-signals-toggle"
            onClick={() => setShowSignals(!showSignals)}
            type="button"
          >
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="fa-icon-margin-right-8"
            />
            Technical Details ({oreRiskFactors.length} signals)
            <FontAwesomeIcon
              icon={showSignals ? faChevronUp : faChevronDown}
              style={{ marginLeft: "auto" }}
            />
          </button>
          {showSignals && (
            <ul className="ore-signals-list">
              {oreRiskFactors.map((factor, i) => (
                <li key={i} className="ore-signal-item">
                  {factor}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default OreInsightCard;
